import React, { Suspense, useEffect, useState } from 'react';
import { initAeroSyncWidget } from 'aerosync-web-sdk';
import { getLogger } from 'src/utils/datadog-logger';
import { useAerosync } from 'src/payments/hooks/use-aerosync-widget';
import { FullPageLoader } from 'shared/components';
import { Aggregator, EnrollmentSteps, IS_E2E } from 'src/payments/constants';
import { BankConnectionContexts } from 'src/payments/hooks';
import { useTrackEnrollmentStepViewed } from 'src/payments/hooks/use-dutchie-pay-analytics/use-track-enrollment-step-viewed';

const logger = getLogger('aerosync-widget');

const MockAggregatorWidget = React.lazy(() => import('src/payments/components/mock-aggregator-widget'));

export function AeroSyncWidget(): JSX.Element | null {
  const [displayMockAeroSyncWidget, setDisplayMockAeroSyncWidget] = useState(false);
  const { aeroSyncWidgetOptions, isReady, AEROSYNC_WIDGET_ID } = useAerosync();
  const aeroSyncWidgetRef = useTrackEnrollmentStepViewed({
    step: EnrollmentSteps.startEnrollment,
    context: BankConnectionContexts.BANK_CONNECTION_AEROSYNC,
  });

  useEffect(() => {
    // In E2E mode show the mock widget immediately — don't wait for the Aerosync
    // SDK to become ready.
    if (IS_E2E) {
      setDisplayMockAeroSyncWidget(true);
      return undefined;
    }

    if (!isReady) {
      return undefined;
    }

    const widgetRef = initAeroSyncWidget(aeroSyncWidgetOptions);
    widgetRef.launch();

    const cleanup = (): void => {
      try {
        widgetRef.destroy();
      } catch (error) {
        logger.error('Aerosync widget error', { error });
      }
    };

    return cleanup;
  }, [isReady, aeroSyncWidgetOptions]);

  if (displayMockAeroSyncWidget && IS_E2E) {
    return (
      <Suspense fallback={null}>
        <MockAggregatorWidget
          aggregator={Aggregator.AEROSYNC}
          onSuccess={(event) => {
            aeroSyncWidgetOptions.onSuccess(event);
            setDisplayMockAeroSyncWidget(false);
          }}
          onExit={() => {
            aeroSyncWidgetOptions.onClose();
            setDisplayMockAeroSyncWidget(false);
          }}
        />
      </Suspense>
    );
  }

  return (
    <>
      <div id={AEROSYNC_WIDGET_ID} ref={aeroSyncWidgetRef} />
      {!isReady && <FullPageLoader />}
    </>
  );
}
